<script setup lang="ts">
import useFuse from '@/composables/useFuse'
import removeStringSpaces from '@/helpers/removeStringSpaces'
import useRoleAbility from '@/modules/roles/composables/useRoleAbility'
import { Role } from '@/modules/roles/composables/useRoles'
import { useQueryClient } from '@tanstack/vue-query'
import { groupBy } from 'lodash'
import { computed, ref, watch } from 'vue'

interface Props { role: Role }
const props = defineProps<Props>()

const queryClient = useQueryClient()
const saveSuccess = ref(false)
const searchQuery = ref('')
const activeModule = ref<string | null>(null)
const modalSearch = ref('')
const addModuleOpen = ref(false)
const addModuleSearch = ref('')

const {
	roleAbilities,
	isLoading,
	isFetching,
	postRoleAbilityMutationAsync,
	postRoleAbilityMutationLoading,
} = useRoleAbility({ roleID: props.role.id, immediate: true })

watch(() => props.role.id, () => {
	searchQuery.value = ''
	activeModule.value = null
	addModuleOpen.value = false
})

const activeCount = computed(() => roleAbilities.value.filter((a) => a.checked).length)
const totalCount = computed(() => roleAbilities.value.length)
const progressPct = computed(() =>
	totalCount.value ? Math.round((activeCount.value / totalCount.value) * 100) : 0
)

const allModules = computed(() => groupBy(roleAbilities.value ?? [], 'module'))

const filteredModules = computed(() => {
	if (!searchQuery.value) return allModules.value
	const fuse = useFuse(roleAbilities.value ?? [], {
		keys: [{ name: 'title', weight: 0.7 }, { name: 'module', weight: 0.3 }],
		threshold: 0.3, shouldSort: true,
	})
	return groupBy(fuse.search(removeStringSpaces(searchQuery.value)).map((r) => r.item), 'module')
})

const isMaster = computed(() => props.role.name === 'Master')

const visibleModuleKeys = (keys: string[]) =>
	keys.filter((m) => m !== '*' || isMaster.value)

const MODULE_GROUPS: { label: string; modules: string[] }[] = [
	{ label: 'Empresa', modules: ['Empresa', 'Roles', 'Grupo', 'Corporativo'] },
	{ label: 'Usuarios', modules: ['Usuario'] },
	{ label: 'Viajes', modules: ['Reservas'] },
	{ label: 'Finanzas', modules: ['CEF', 'Cartas garantía'] },
	{ label: 'Operaciones', modules: ['Aprobaciones', 'Políticas'] },
	{ label: 'Sistema', modules: ['*', 'Subscription', 'Permisos', 'General'] },
]

const getModuleGroup = (module: string): string => {
	const found = MODULE_GROUPS.find((g) => g.modules.includes(module))
	return found?.label ?? 'Otros'
}

const activeModules = computed(() =>
	visibleModuleKeys(Object.keys(filteredModules.value).filter((m) => moduleActiveCount(m) > 0))
)

const inactiveModules = computed(() =>
	visibleModuleKeys(Object.keys(allModules.value).filter((m) => moduleActiveCount(m) === 0))
)

const groupedActiveModules = computed(() => {
	const grouped = groupBy(activeModules.value, getModuleGroup)
	return MODULE_GROUPS
		.map((g) => ({ label: g.label, modules: grouped[g.label] ?? [] }))
		.filter((g) => g.modules.length > 0)
		.concat(grouped['Otros']?.length ? [{ label: 'Otros', modules: grouped['Otros'] }] : [])
})

const groupedInactiveModules = computed(() => {
	const src = addModuleSearch.value
		? inactiveModules.value.filter((m) => m.toLowerCase().includes(addModuleSearch.value.toLowerCase()))
		: inactiveModules.value
	const grouped = groupBy(src, getModuleGroup)
	return MODULE_GROUPS
		.map((g) => ({ label: g.label, modules: grouped[g.label] ?? [] }))
		.filter((g) => g.modules.length > 0)
		.concat(grouped['Otros']?.length ? [{ label: 'Otros', modules: grouped['Otros'] }] : [])
})



const modulePermissions = computed(() => {
	if (!activeModule.value) return []
	const perms = allModules.value[activeModule.value] ?? []
	if (!modalSearch.value) return perms
	const fuse = useFuse(perms, {
		keys: [{ name: 'title', weight: 1 }], threshold: 0.3, shouldSort: true,
	})
	return fuse.search(removeStringSpaces(modalSearch.value)).map((r) => r.item)
})

const getPermissionSubGroup = (name: string): string => {
	const parts = name.split('-')
	if (parts.length <= 1) return ''
	const sub = parts.slice(1).join('-')
	return sub === 'any' ? '' : sub
}

const modulePermissionGroups = computed(() => {
	const grouped = groupBy(modulePermissions.value, (p) => getPermissionSubGroup(p.name))
	const general = grouped[''] ?? []
	const rest = Object.entries(grouped)
		.filter(([key]) => key !== '')
		.sort(([a], [b]) => a.localeCompare(b))
	return [
		...(general.length ? [{ key: '', label: 'General', items: general }] : []),
		...rest.map(([key, items]) => ({
			key,
			label: key.charAt(0).toUpperCase() + key.slice(1).replace(/-/g, ' '),
			items,
		})),
	]
})

const moduleActiveCount = (module: string) =>
	(allModules.value[module] ?? []).filter((a) => a.checked).length

const moduleTotal = (module: string) => (allModules.value[module] ?? []).length

const togglePermission = (id: number) => {
	const a = roleAbilities.value.find((x) => x.id === id)
	if (a) a.checked = !a.checked
}

const toggleAllInModule = (module: string, val: boolean) => {
	;(allModules.value[module] ?? []).forEach((a) => { a.checked = val })
}

const openModule = (module: string) => {
	addModuleOpen.value = false
	activeModule.value = module
	modalSearch.value = ''
}

const MODULE_ICONS: Record<string, string> = {
	Empresa: 'business',
	Usuario: 'person',
	Reservas: 'travel_explore',
	CEF: 'receipt_long',
	Aprobaciones: 'approval_delegation',
	Políticas: 'policy',
	Roles: 'shield_person',
	Grupo: 'group',
	Corporativo: 'corporate_fare',
	Permisos: 'key',
	Subscription: 'subscriptions',
	'Cartas garantía': 'description',
	General: 'settings',
	'*': 'admin_panel_settings',
}
const moduleIcon = (name: string) => MODULE_ICONS[name] ?? 'lock'

// El backend nombra este módulo "CEF"; en UI se muestra como "Itinerarios"
const MODULE_DISPLAY_NAMES: Record<string, string> = { CEF: 'Itinerarios' }
const moduleDisplayName = (name: string) => MODULE_DISPLAY_NAMES[name] ?? name

const handleSave = async () => {
	const ids = roleAbilities.value.filter((a) => a.checked).map((a) => a.id)
	// Si el POST rechaza (postRoleAbility re-lanza), cortamos aquí: no
	// invalidamos (para no pisar los toggles del usuario con el estado del
	// server) ni marcamos éxito, y dejamos que el error se propague al guard.
	await postRoleAbilityMutationAsync(ids)
	await queryClient.invalidateQueries(['roleAbility', props.role.id])
	saveSuccess.value = true
	setTimeout(() => { saveSuccess.value = false }, 2000)
}

// El botón del panel traga el error (el toast ya avisa) para no dejar un
// unhandled rejection; el guard del padre necesita el rechazo, así que expone
// el handleSave crudo.
const handleSaveFromPanel = async () => {
	try {
		await handleSave()
	} catch {
		// El toast de error ya se mostró en postRoleAbility.
	}
}

// El padre (IndexPage /roles, RolesTab de empresa) dispara este guardado desde
// el modal de "guardado obligatorio" antes de permitir cambiar de rol; save()
// RECHAZA en fallo para que el guard no cambie de rol.
defineExpose({ save: handleSave })
</script>

<template>
	<div class="flex flex-col h-full">
		<!-- Header -->
		<div class="px-5 py-4 border-b border-gray-100 flex-shrink-0">
			<div class="flex items-center justify-between gap-3 mb-3">
				<div class="flex items-center gap-2.5">
					<div class="w-8 h-8 rounded-lg bg-[#021F58]/10 flex items-center justify-center">
						<span class="material-symbols-outlined text-sm text-[#021F58] leading-none">shield_person</span>
					</div>
					<div>
						<h3 class="text-sm font-semibold text-gray-900">{{ role.name }}</h3>
						<p class="text-xs text-gray-400">{{ activeCount }}/{{ totalCount }} permisos</p>
					</div>
				</div>
				<span
					class="text-xs font-bold px-2.5 py-1 rounded-full"
					:class="progressPct === 0 ? 'bg-gray-100 text-gray-400'
						: progressPct === 100 ? 'bg-emerald-100 text-emerald-700'
						: 'bg-[#021F58]/10 text-[#021F58]'"
				>{{ progressPct }}%</span>
			</div>

			<div class="h-1 bg-gray-100 rounded-full overflow-hidden mb-3">
				<div
					class="h-full rounded-full transition-all duration-500"
					:class="progressPct === 100 ? 'bg-emerald-500' : 'bg-[#021F58]'"
					:style="{ width: `${progressPct}%` }"
				/>
			</div>

			<div class="relative">
				<span class="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-sm text-gray-400 leading-none">search</span>
				<input
					v-model="searchQuery"
					type="text"
					placeholder="Buscar módulo o permiso..."
					class="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#021F58]/20 bg-gray-50 placeholder-gray-400"
				/>
			</div>
		</div>

		<!-- Module list -->
		<div class="flex-1 overflow-y-auto p-4">
			<div v-if="isLoading || isFetching" class="space-y-2">
				<div v-for="i in 4" :key="i" class="h-12 rounded-xl bg-gray-100 animate-pulse" />
			</div>

			<div
				v-else-if="!activeModules.length && !searchQuery"
				class="flex flex-col items-center justify-center py-12 text-gray-400"
			>
				<div class="w-12 h-12 rounded-2xl border-2 border-dashed border-gray-200 flex items-center justify-center mb-3">
					<span class="material-symbols-outlined text-xl text-gray-300">shield_person</span>
				</div>
				<p class="text-sm font-medium text-gray-400">Sin permisos asignados</p>
				<p class="text-xs text-gray-300 mt-1">Agrega módulos para comenzar</p>
			</div>

			<div v-else-if="!activeModules.length && searchQuery" class="flex flex-col items-center justify-center py-12 text-gray-400">
				<span class="material-symbols-outlined text-3xl mb-2">search_off</span>
				<p class="text-sm">Sin resultados</p>
			</div>

			<div v-else class="space-y-3">
				<div v-for="group in groupedActiveModules" :key="group.label">
					<p class="text-[10px] font-bold uppercase tracking-widest text-gray-400 px-1 mb-1.5">{{ group.label }}</p>
					<div class="space-y-1">
						<button
							v-for="module in group.modules"
							:key="module"
							type="button"
							class="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left transition-all duration-150 border group bg-[#021F58]/5 border-[#021F58]/15 hover:bg-[#021F58]/10"
							@click="openModule(module)"
						>
							<div class="w-7 h-7 rounded-lg bg-[#021F58]/10 flex items-center justify-center flex-shrink-0">
								<span class="material-symbols-outlined text-xs text-[#021F58] leading-none">{{ moduleIcon(module) }}</span>
							</div>
							<div class="flex-1 min-w-0">
								<div class="flex items-center justify-between gap-1 mb-1">
									<p class="text-xs font-semibold text-gray-900 truncate">{{ moduleDisplayName(module) }}</p>
									<span class="text-[10px] tabular-nums text-[#021F58]/60 flex-shrink-0">{{ moduleActiveCount(module) }}/{{ moduleTotal(module) }}</span>
								</div>
								<div class="h-1 bg-[#021F58]/10 rounded-full overflow-hidden">
									<div
										class="h-full bg-[#021F58] rounded-full transition-all duration-500"
										:style="{ width: `${moduleTotal(module) ? (moduleActiveCount(module) / moduleTotal(module)) * 100 : 0}%` }"
									/>
								</div>
							</div>
							<span class="material-symbols-outlined text-xs text-[#021F58]/30 leading-none flex-shrink-0 group-hover:text-[#021F58]/60 transition-colors">chevron_right</span>
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Footer -->
		<div class="flex-shrink-0 border-t border-gray-100 px-5 py-3 bg-gray-50/50 flex items-center justify-between gap-2">
			<button
				v-if="inactiveModules.length"
				type="button"
				class="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border border-dashed border-gray-300 text-gray-500 hover:border-[#021F58]/30 hover:text-[#021F58] hover:bg-[#021F58]/5 transition-all duration-150"
				@click="addModuleOpen = true; addModuleSearch = ''"
			>
				<span class="material-symbols-outlined text-base leading-none">add</span>
				Agregar módulo
			</button>
			<p v-else class="text-xs text-gray-400">{{ activeCount }} permiso{{ activeCount !== 1 ? 's' : '' }} activo{{ activeCount !== 1 ? 's' : '' }}</p>

			<button
				type="button"
				class="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 disabled:opacity-60 ml-auto"
				:class="saveSuccess ? 'bg-emerald-500 text-white' : 'bg-[#021F58] text-white hover:bg-[#021F58]/90'"
				:disabled="postRoleAbilityMutationLoading"
				@click="handleSaveFromPanel"
			>
				<span class="material-symbols-outlined text-base leading-none" :class="postRoleAbilityMutationLoading ? 'animate-spin' : ''">
					{{ postRoleAbilityMutationLoading ? 'progress_activity' : saveSuccess ? 'check' : 'save' }}
				</span>
				{{ saveSuccess ? 'Guardado' : postRoleAbilityMutationLoading ? 'Guardando...' : 'Guardar' }}
			</button>
		</div>
	</div>

	<Teleport to="body">
		<!-- Add module modal -->
		<Transition
			enter-active-class="transition-all duration-200 ease-out"
			enter-from-class="opacity-0 scale-95"
			enter-to-class="opacity-100 scale-100"
			leave-active-class="transition-all duration-150 ease-in"
			leave-from-class="opacity-100 scale-100"
			leave-to-class="opacity-0 scale-95"
		>
			<div v-if="addModuleOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4">
				<div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="addModuleOpen = false" />
				<div class="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden">
					<div class="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
						<div>
							<h3 class="text-sm font-bold text-gray-900">Agregar módulo</h3>
							<p class="text-xs text-gray-400 mt-0.5">Selecciona un módulo para configurar sus permisos</p>
						</div>
						<button
							type="button"
							class="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-50 transition-colors flex-shrink-0"
							@click="addModuleOpen = false"
						>
							<span class="material-symbols-outlined text-base leading-none">close</span>
						</button>
					</div>

					<div class="px-4 py-3 border-b border-gray-100 flex-shrink-0">
						<div class="relative">
							<span class="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-sm text-gray-400 leading-none">search</span>
							<input
								v-model="addModuleSearch"
								type="text"
								placeholder="Buscar módulo..."
								class="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#021F58]/20 bg-gray-50 placeholder-gray-400"
								autofocus
							/>
						</div>
					</div>

					<div class="overflow-y-auto flex-1 px-4 py-4 space-y-5">
						<div v-if="!groupedInactiveModules.length" class="flex flex-col items-center justify-center py-8 text-gray-400">
							<span class="material-symbols-outlined text-2xl mb-1">search_off</span>
							<p class="text-xs">Sin resultados</p>
						</div>

						<div v-for="group in groupedInactiveModules" :key="group.label">
							<p class="text-[10px] font-bold uppercase tracking-widest text-gray-400 px-1 mb-2">{{ group.label }}</p>
							<div class="grid grid-cols-3 gap-2">
								<button
									v-for="module in group.modules"
									:key="module"
									type="button"
									class="flex flex-col gap-2 p-3.5 rounded-xl text-left transition-all duration-150 border border-gray-200 hover:border-[#021F58]/30 hover:bg-[#021F58]/5 group"
									@click="openModule(module)"
								>
									<div class="flex items-center justify-between">
										<div class="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center group-hover:bg-[#021F58]/10 transition-colors">
											<span class="material-symbols-outlined text-sm text-gray-400 leading-none group-hover:text-[#021F58] transition-colors">{{ moduleIcon(module) }}</span>
										</div>
										<span class="text-[10px] font-semibold tabular-nums text-gray-400">{{ moduleTotal(module) }}</span>
									</div>
									<div>
										<p class="text-sm font-medium text-gray-600 truncate group-hover:text-gray-900 transition-colors">{{ moduleDisplayName(module) }}</p>
										<p class="text-[10px] text-gray-400 mt-0.5">{{ moduleTotal(module) }} permisos</p>
									</div>
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</Transition>

		<!-- Module permissions modal -->
		<Transition
			enter-active-class="transition-all duration-200 ease-out"
			enter-from-class="opacity-0 scale-95"
			enter-to-class="opacity-100 scale-100"
			leave-active-class="transition-all duration-150 ease-in"
			leave-from-class="opacity-100 scale-100"
			leave-to-class="opacity-0 scale-95"
		>
			<div v-if="activeModule" class="fixed inset-0 z-50 flex items-center justify-center p-4">
				<div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="activeModule = null" />

				<div class="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
					<div class="px-5 py-4 flex items-center justify-between flex-shrink-0 border-b border-gray-100">
						<div class="flex items-center gap-3">
							<div class="w-9 h-9 rounded-xl bg-[#021F58]/10 flex items-center justify-center">
								<span class="material-symbols-outlined text-[#021F58] leading-none">{{ moduleIcon(activeModule) }}</span>
							</div>
							<div>
								<h3 class="text-sm font-bold text-gray-900">{{ moduleDisplayName(activeModule) }}</h3>
								<p class="text-xs text-gray-400">
									{{ moduleActiveCount(activeModule) }}/{{ moduleTotal(activeModule) }} activos
								</p>
							</div>
						</div>
						<div class="flex items-center gap-2">
							<button
								type="button"
								class="text-xs font-semibold px-2.5 py-1 rounded-lg border transition-colors"
								:class="moduleActiveCount(activeModule) < moduleTotal(activeModule)
									? 'border-[#021F58]/20 text-[#021F58] bg-[#021F58]/5 hover:bg-[#021F58]/10'
									: 'border-red-200 text-red-500 bg-red-50 hover:bg-red-100'"
								@click="toggleAllInModule(activeModule, moduleActiveCount(activeModule) < moduleTotal(activeModule))"
							>
								{{ moduleActiveCount(activeModule) < moduleTotal(activeModule) ? 'Todos' : 'Ninguno' }}
							</button>
							<button
								type="button"
								class="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-50 transition-colors"
								@click="activeModule = null"
							>
								<span class="material-symbols-outlined text-base leading-none">close</span>
							</button>
						</div>
					</div>

					<div class="px-4 py-3 border-b border-gray-100 flex-shrink-0">
						<div class="relative">
							<span class="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-sm text-gray-400 leading-none">search</span>
							<input
								v-model="modalSearch"
								type="text"
								placeholder="Filtrar permisos..."
								class="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#021F58]/20 bg-gray-50 placeholder-gray-400"
							/>
						</div>
					</div>

					<div class="overflow-y-auto flex-1 px-4 py-4 space-y-5">
						<div v-if="!modulePermissions.length" class="flex flex-col items-center justify-center py-8 text-gray-400">
							<span class="material-symbols-outlined text-2xl mb-1">search_off</span>
							<p class="text-xs">Sin resultados</p>
						</div>

						<div v-for="group in modulePermissionGroups" :key="group.key">
							<p class="text-[10px] font-bold uppercase tracking-widest text-gray-400 px-1 mb-2">{{ group.label }}</p>
							<div class="grid grid-cols-1 sm:grid-cols-2 gap-1">
								<button
									v-for="permission in group.items"
									:key="permission.id"
									type="button"
									class="flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all duration-150 group border-l-2"
									:class="permission.checked
										? 'border-[#021F58] bg-[#021F58]/5'
										: 'border-transparent hover:border-gray-200 hover:bg-gray-50'"
									@click="togglePermission(permission.id)"
								>
									<div
										class="w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all"
										:class="permission.checked
											? 'bg-[#021F58] border-[#021F58]'
											: 'border-gray-300 group-hover:border-gray-400'"
									>
										<span v-if="permission.checked" class="material-symbols-outlined text-[10px] text-white leading-none">check</span>
									</div>
									<span
										class="text-sm flex-1 transition-colors truncate"
										:class="permission.checked ? 'text-[#021F58] font-medium' : 'text-gray-600'"
									>{{ permission.label || permission.title }}</span>
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</Transition>
	</Teleport>
</template>
